'use client'

import { memo, useMemo, type ComponentType } from 'react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

export type MarketingLogoTickerItem = {
  id: string
  label: string
  icon: ComponentType<{ className?: string }>
}

export type MarketingLogoTickerProps = {
  items: readonly MarketingLogoTickerItem[]
  label?: string
  dual?: boolean
  className?: string
}

const TickerRow = memo(function TickerRow({
  items,
  reverse,
  reduced,
}: {
  items: readonly MarketingLogoTickerItem[]
  reverse?: boolean
  reduced: boolean
}) {
  // baby: Duplicate once so translate(-50%) loops without a jump
  const loop = useMemo(
    () => (reduced ? [...items] : [...items, ...items]),
    [items, reduced],
  )

  return (
    <div className="ninxl-marquee-mask relative overflow-hidden py-1.5">
      <div
        className={cn(
          'flex w-max items-center gap-3',
          reduced
            ? 'flex-wrap justify-center'
            : reverse
              ? 'ninxl-marquee-reverse'
              : 'ninxl-marquee-forward',
        )}
      >
        {loop.map((item, index) => {
          const Icon = item.icon
          return (
            <span
              key={`${item.id}-${index}`}
              className="inline-flex shrink-0 items-center gap-2 border border-[color:var(--ninxl-8bit-border,#21728d)]/35 bg-[color:var(--ninxl-8bit-panel,#f7f4ef)] px-3.5 py-2 text-sm font-medium text-[color:var(--ninxl-theme-fg)]"
            >
              <Icon className="h-3.5 w-3.5 text-[color:var(--ninxl-brand-teal,#21728d)]" />
              {item.label}
            </span>
          )
        })}
      </div>
    </div>
  )
})

/**
 * baby: Webflow home / landing logo ticker: icon pills scrolling
 * opposite directions (flex-scrolling / logo-wrap-ticker).
 */
export const MarketingLogoTicker = memo(function MarketingLogoTicker({
  items,
  label = 'Works with',
  dual = true,
  className,
}: MarketingLogoTickerProps) {
  const reduced = Boolean(useReducedMotion())

  if (items.length === 0) {
    return null
  }

  return (
    <section
      className={cn('overflow-hidden py-2', className)}
      aria-label={label}
      data-section="logo-ticker"
    >
      <TickerRow items={items} reduced={reduced} />
      {dual ? <TickerRow items={items} reverse reduced={reduced} /> : null}
    </section>
  )
})
